import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiAward, FiCamera, FiHeart } = FiIcons;

const Guide = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const testimonials = [
    {
      text: "We saw baby elephants being fed... Mohan knew exactly where to go.",
      author: "Sarah M.",
      platform: "Agoda"
    },
    {
      text: "The best thing was our ranger... knowledgeable... always made sure we were happy.",
      author: "David L.",
      platform: "Booking.com"
    },
    {
      text: "Best Safari tour guy - Mohan... We were the first at the park and he showed us some hidden gems.",
      author: "Emma R.",
      platform: "TripAdvisor"
    }
  ];

  const achievements = [
    { icon: FiAward, label: "20 Years", subtitle: "Safari Experience" },
    { icon: FiStar, label: "9.1/10", subtitle: "Guest Rating" },
    { icon: FiCamera, label: "1000+", subtitle: "Wildlife Photos" },
    { icon: FiHeart, label: "500+", subtitle: "Happy Guests" }
  ];

  return (
    <section id="guide" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1 }}
            className="space-y-8"
          >
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1, delay: 0.2 }}
                className="text-4xl md:text-5xl font-bold text-white mb-6"
              >
                Meet <span className="gradient-text">Mohan</span>
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1, delay: 0.4 }}
                className="text-xl text-white/80 leading-relaxed mb-8"
              >
                Your expert safari guide with two decades of experience tracking wildlife in Udawalawe. 
                Mohan doesn't just show you animals—he tells you their stories, reveals their secrets, 
                and ensures every safari becomes an unforgettable adventure.
              </motion.p>
            </div>

            {/* Achievements Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="glass-card p-6 rounded-2xl text-center"
                >
                  <SafeIcon 
                    icon={achievement.icon} 
                    className="text-3xl gradient-text mx-auto mb-3" 
                  />
                  <div className="text-2xl font-bold text-white mb-1">
                    {achievement.label}
                  </div>
                  <div className="text-white/60 text-sm">
                    {achievement.subtitle}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.8 }}
              className="flex flex-wrap gap-4"
            >
              <div className="glass-morphism px-4 py-2 rounded-full flex items-center space-x-2">
                <SafeIcon icon={FiStar} className="text-yellow-400" />
                <span className="text-white font-medium">Booking.com 9.1</span>
              </div>
              <div className="glass-morphism px-4 py-2 rounded-full flex items-center space-x-2">
                <SafeIcon icon={FiStar} className="text-yellow-400" />
                <span className="text-white font-medium">Agoda 8.9</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Photo & Testimonials */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.3 }}
            className="space-y-8"
          >
            {/* Guide Photo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="relative"
            >
              <div className="glass-morphism p-4 rounded-3xl">
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Mohan - Safari Guide"
                  className="w-full h-96 object-cover rounded-2xl"
                />
              </div>
              <motion.div
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 1 }}
                className="absolute -top-4 -right-4 glass-morphism p-4 rounded-full"
              >
                <SafeIcon icon={FiAward} className="text-2xl gradient-text" />
              </motion.div>
            </motion.div>

            {/* Testimonials Slider */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.6 }}
              className="space-y-4"
            >
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="glass-card p-6 rounded-2xl"
                >
                  <p className="text-white/90 italic mb-3">
                    "{testimonial.text}"
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">
                      {testimonial.author}
                    </span>
                    <span className="text-white/60 text-sm">
                      {testimonial.platform}
                    </span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Guide;